import React, { useState } from 'react';
import {
  Row, Col, Card, Form, Button, Spinner, Alert
} from 'react-bootstrap';
import { FaFileDownload, FaEye } from 'react-icons/fa';
import axios from 'axios';
import Select from 'react-select';

const GdprPolicyGeneratorTab = ({ userData, showNotification, region, refreshActivities, logToAccessLogs }) => {
  const [form, setForm] = useState({
    orgName: '', industry: '', location: '',
    activities: [], policyTypes: [],
    dpoContactDetails: '', legalBasis: []
  });
  const [loading, setLoading] = useState(false);
  const [generatedPolicies, setGeneratedPolicies] = useState([]);

  const GDPR_ACTIVITY_OPTIONS = [
    { value: 'Customer Relationship Management', label: 'Customer Relationship Management' },
    { value: 'Marketing & Communications', label: 'Marketing & Communications' },
    { value: 'Employee Data Processing', label: 'Employee Data Processing' },
    { value: 'Financial Transactions', label: 'Financial Transactions' },
    { value: 'Website Analytics & Cookies', label: 'Website Analytics & Cookies' },
    { value: 'Health Data Processing', label: 'Health Data Processing' },
    { value: 'Video Surveillance/CCTV', label: 'Video Surveillance/CCTV' },
    { value: 'Direct Marketing', label: 'Direct Marketing' },
    { value: 'Profiling & Automated Decision Making', label: 'Profiling & Automated Decision Making' }
  ];

  const GDPR_LEGAL_BASIS_OPTIONS = [
    { value: 'Consent', label: 'Consent (Art. 6(1)(a))' },
    { value: 'Contract', label: 'Contract (Art. 6(1)(b))' },
    { value: 'Legal Obligation', label: 'Legal Obligation (Art. 6(1)(c))' },
    { value: 'Vital Interests', label: 'Vital Interests (Art. 6(1)(d))' },
    { value: 'Public Task', label: 'Public Task (Art. 6(1)(e))' },
    { value: 'Legitimate Interests', label: 'Legitimate Interests (Art. 6(1)(f))' }
  ];

  const headers = {
    Region: region,
    UserId: userData?.['cognito:username']
  };

  const logActivity = async (message, badge = 'info') => {
    try {
      const payload = { action: badge, module: 'Policy Generator', details: message };
      await axios.post(
        'https://w8wvx2z3f4.execute-api.ap-southeast-2.amazonaws.com/prod/api/activity-log/save',
        payload,
        { headers }
      );
      if (refreshActivities) await refreshActivities();
    } catch (err) {
      console.warn('Failed to log activity:', err.message);
    }
  };

  const handleReset = () => {
    setForm({ orgName: '', industry: '', location: '', activities: [], policyTypes: [], dpoContactDetails: '', legalBasis: [] });
    setGeneratedPolicies([]);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      policyTypes: checked ? [...prev.policyTypes, value] : prev.policyTypes.filter((p) => p !== value)
    }));
  };

  const handlePreview = async () => {
    if (form.policyTypes.length === 0) {
      showNotification('Select at least one policy to preview.', 'warning');
      return;
    }
    const previewType = form.policyTypes[0];
    const typeSlug = previewType.toLowerCase().replace(/\s+/g, '-');
    try {
      const res = await axios.get(
        `https://5nlylzjya3.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/policy-generator/sample/${typeSlug}`,
        { headers, responseType: 'blob' }
      );
      const file = new Blob([res.data], { type: 'text/plain' });
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
      await logActivity(`Previewed GDPR ${previewType} sample policy`, 'info');
      logToAccessLogs?.({
        action:      'Policy Previewed',
        severity:    'info',
        description: `Previewed GDPR sample policy: ${previewType}`,
        metadata: { policyType: previewType }
      });
    } catch (err) {
      console.error('Error previewing policy:', err);
      showNotification('Error previewing policy', 'danger');
    }
  };

  const downloadPolicy = async (policy) => {
    try {
      const res = await axios.get(
        `https://cyzc6ry5ha.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/policy-generator/download/${policy.id}`,
        { headers, responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GDPR_${policy.name.replace(/\s+/g, '_')}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      await logActivity(`Downloaded generated ${policy.name} for ${form.orgName}`, 'info');
      logToAccessLogs?.({
        action:      'Policy Downloaded',
        severity:    'info',
        description: `Downloaded GDPR policy: ${policy.name} for ${form.orgName}`,
        metadata: { policyId: policy.id, policyName: policy.name, orgName: form.orgName }
      });
    } catch (error) {
      console.error('Error downloading policy:', error);
      showNotification('Error downloading policy', 'danger');
    }
  };

  const handleGenerate = async () => {
    if (!form.orgName || !form.industry || !form.location || form.policyTypes.length === 0) {
      showNotification('Please fill all required fields.', 'danger');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        'https://pw417f6r0c.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/policy-generator/create',
        { ...form },
        { headers }
      );
      setGeneratedPolicies(res.data.generated || []);
      showNotification('GDPR-compliant policies generated successfully!');
      await logActivity(`Generated ${form.policyTypes.length} GDPR policies for ${form.orgName}`, 'success');
      logToAccessLogs?.({
        action:      'Policy Generated',
        severity:    'success',
        description: `Generated ${res.data.generated?.length ?? 0} GDPR policies for ${form.orgName} (${form.industry})`,
        metadata: {
          orgName:     form.orgName,
          industry:    form.industry,
          policyTypes: form.policyTypes,
          count:       res.data.generated?.length,
        }
      });
    } catch (err) {
      console.error('Error generating policies:', err);
      showNotification('Error generating policies', 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <Card.Header><h5>Generate GDPR-Compliant Policies</h5></Card.Header>
      <Card.Body>
        <Alert variant="info">
          <strong>GDPR Policy Generator:</strong> All policies include required GDPR elements such as legal basis,
          data subject rights, and DPO contact information.
        </Alert>
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Organization Name *</Form.Label>
                <Form.Control type="text" name="orgName" value={form.orgName} onChange={handleChange} placeholder="Your organization name" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Industry Sector *</Form.Label>
                <Form.Select name="industry" value={form.industry} onChange={handleChange}>
                  <option value="">Select Industry</option>
                  <option value="Financial Services">Financial Services</option>
                  <option value="Healthcare & Pharmaceuticals">Healthcare & Pharmaceuticals</option>
                  <option value="E-commerce & Retail">E-commerce & Retail</option>
                  <option value="Education">Education</option>
                  <option value="Technology & Software">Technology & Software</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Telecommunications">Telecommunications</option>
                  <option value="Media & Entertainment">Media & Entertainment</option>
                  <option value="Transportation & Logistics">Transportation & Logistics</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Professional Services">Professional Services</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Primary Business Location *</Form.Label>
            <Form.Select name="location" value={form.location} onChange={handleChange}>
              <option value="">Select Location</option>
              <option value="European Union">European Union</option>
              <option value="European Economic Area">European Economic Area</option>
              <option value="EU + International Operations">EU + International Operations</option>
              <option value="United Kingdom">United Kingdom</option>
              <option value="Switzerland">Switzerland</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Data Processing Activities</Form.Label>
            <Select isMulti options={GDPR_ACTIVITY_OPTIONS}
              value={GDPR_ACTIVITY_OPTIONS.filter(opt => form.activities.includes(opt.value))}
              onChange={(selected) => setForm({ ...form, activities: selected.map(s => s.value) })}
              placeholder="Select all applicable processing activities..." />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Legal Basis for Processing</Form.Label>
            <Select isMulti options={GDPR_LEGAL_BASIS_OPTIONS}
              value={GDPR_LEGAL_BASIS_OPTIONS.filter(opt => form.legalBasis.includes(opt.value))}
              onChange={(selected) => setForm({ ...form, legalBasis: selected.map(s => s.value) })}
              placeholder="Select applicable legal basis..." />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Data Protection Officer Contact</Form.Label>
            <Form.Control type="text" name="dpoContactDetails" value={form.dpoContactDetails} onChange={handleChange}
              placeholder="e.g., dpo@yourcompany.com" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Policy Types to Generate *</Form.Label>
            <Row>
              {[
                'GDPR Privacy Notice', 'Cookie Policy', 'Data Retention Policy',
                'Consent Management Policy', 'Data Subject Rights Policy',
                'Data Breach Response Policy', 'Data Processing Agreement (DPA)',
                'Privacy Impact Assessment Template'
              ].map((type) => (
                <Col md={6} key={type}>
                  <Form.Check type="checkbox" id={type} label={type} value={type}
                    checked={form.policyTypes.includes(type)} onChange={handleCheckboxChange} />
                </Col>
              ))}
            </Row>
          </Form.Group>
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={handleGenerate} disabled={loading}>
              {loading && <Spinner as="span" animation="border" size="sm" className="me-2" />}
              Generate Policies
            </Button>
            <Button variant="secondary" onClick={handleReset}>Reset</Button>
            <Button variant="outline-info" onClick={handlePreview} disabled={form.policyTypes.length === 0}>
              <FaEye /> Preview Sample
            </Button>
          </div>
        </Form>
        <hr />
        <div>
          <h6>Generated GDPR Policies</h6>
          {generatedPolicies.length === 0 ? (
            <Alert variant="secondary">No policies generated yet.</Alert>
          ) : (
            <Row>
              {generatedPolicies.map((policy, idx) => (
                <Col md={6} key={idx} className="mb-3">
                  <Card>
                    <Card.Body>
                      <Card.Title>{policy.name}</Card.Title>
                      <Card.Text className="text-muted">{policy.description}</Card.Text>
                      <Button variant="outline-primary" size="sm" onClick={() => downloadPolicy(policy)}>
                        <FaFileDownload /> Download
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default GdprPolicyGeneratorTab;